class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onSubmitHandler = this.onSubmitHandler.bind(this);

    const facetForm = this.querySelector('form');

    // Filters and price are only applied when the Apply button is clicked (see bindApplyButton).
    // Sort applies immediately (see bindSortChangeEvents), same as it always has.
    this.bindSortChangeEvents(facetForm);
    this.bindApplyButton(facetForm);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  // Sort is a view preference, not a filter selection — it stays instant. Building the params
  // from the current URL (the last-applied state) rather than the live form means an in-progress,
  // not-yet-applied filter/price edit in the drawer can't get applied early by a sort change.
  bindSortChangeEvents(facetForm) {
    facetForm.querySelectorAll('select[name="sort_by"]').forEach((sortInput) => {
      sortInput.addEventListener('change', (event) => {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', event.target.value);
        params.delete('page');
        this.onSubmitForm(params.toString(), event);
      });
    });
  }

  bindApplyButton(facetForm) {
    const applyButton = facetForm.querySelector('.mobile-facets__footer button[type="button"]');
    if (!applyButton) return;

    applyButton.addEventListener('click', this.onSubmitHandler);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      let searchParams;

      if (event.state && event.state.searchParams !== undefined) {
        // State was stored by updateUrlPage or updateURLHash — use it directly.
        // This already has page stripped, so applyFilters correctly goes to page 1.
        searchParams = event.state.searchParams;
      } else {
        // No state (e.g. initial page load pushed onto stack by the browser).
        // Fall back to initial params but strip page so filter state is restored
        // without trying to resume a specific page number via applyFilters.
        const initial = new URLSearchParams(FacetFiltersForm.searchParamsInitial);
        initial.delete('page');
        searchParams = initial.toString();
      }

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      const nostoSearchGrid = window.__nostoSearchGrid;
      if (nostoSearchGrid && nostoSearchGrid.ready) {
        nostoSearchGrid.applyFilters(searchParams);
        FacetFiltersForm.searchParamsPrev = searchParams;
        return;
      }

      const nostoCollectionGrid = window.__nostoCollectionGrid;
      if (nostoCollectionGrid && nostoCollectionGrid.ready) {
        nostoCollectionGrid.applyFilters(searchParams);
        FacetFiltersForm.searchParamsPrev = searchParams;
        return;
      }

      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const gridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = gridContainer ? gridContainer.querySelector('.collection') : null;

    if (collectionEl) {
      collectionEl.classList.add('loading');
    }

    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        // FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderAppliedFiltersCount(html);
        FacetFiltersForm.renderAppliedFilterCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    // FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.renderAppliedFiltersCount(html);
    FacetFiltersForm.renderAppliedFilterCount(html);
  }

  static renderProductGridContainer(html) {
    const nostoGrid = window.__nostoCollectionGrid;
    
    if (nostoGrid && nostoGrid.ready) {
      // Update ShopifyCollectionFallback when Nosto returns 0
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const newFallback = parsedHTML.getElementById('ShopifyCollectionFallback');
      const currentFallback = document.getElementById('ShopifyCollectionFallback');

      if (newFallback && currentFallback) {
        currentFallback.innerHTML = newFallback.innerHTML;
      }
      return;
    }

    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderAppliedFiltersCount(html) {
    const filtersCount = document.querySelector('.mobile-facets__clear-wrapper .filters__count');

    if (!filtersCount) return;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const checkedCount = parsedHTML.querySelectorAll('.mobile-facets__checkbox[checked]').length;

    const priceMin = parsedHTML.querySelector('input[name="filter.v.price.gte"]');
    const priceMax = parsedHTML.querySelector('input[name="filter.v.price.lte"]');
    const hasPriceFilter = (priceMin?.value !== '' && priceMin?.value != null) || (priceMax?.value !== '' && priceMax?.value != null);
    const priceCount = hasPriceFilter ? 1 : 0;

    const count = checkedCount + priceCount;

    filtersCount.innerHTML = `&nbsp;(${count})`;
  }

  static renderAppliedFilterCount(html) {
    const allDetailsElements = new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.mobile-facets__details:not(.facet-sort, .facet-price)');

    allDetailsElements.forEach((element) => {
      const index = element.dataset.index;
      const count = element.querySelectorAll('.mobile-facets__checkbox[checked]').length;
      const target = document.querySelector(`.mobile-facets__details[data-index="${index}"] .filter_active_values_count`);

      if (!target) return;

      target.innerHTML = count > 0 ? `(${count})` : "";
    });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    container.innerHTML = count;
    container.classList.remove('loading');

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    // Build the set of filter keys present in this server response.
    const responseKeys = new Set(
      Array.from(facetDetailsElements)
        .map(el => el.dataset.filterKey)
        .filter(Boolean)
    );

    // Hide any live filter that the server response no longer includes,
    // and re-show any that has come back — without removing elements from the DOM
    // (which would destroy Accordion / custom-element event listeners).
    // IMPORTANT: also disable/enable inputs so FormData doesn't serialize
    // checked inputs from currently-hidden filters.
    document.querySelectorAll('.js-filter[data-filter-key]').forEach((domFilter) => {
      const key = domFilter.dataset.filterKey;
      if (key && !responseKeys.has(key)) {
        domFilter.hidden = true;
        domFilter.querySelectorAll('input, select').forEach(el => { el.disabled = true; });
      } else if (key && responseKeys.has(key)) {
        domFilter.hidden = false;
        domFilter.querySelectorAll('input, select').forEach(el => { el.disabled = false; });
      }
    });

    facetsToRender.forEach((element) => {
      const filterKey = element.dataset.filterKey;
      if (!filterKey) return;

      // Update ALL instances of this filter (desktop sidebar, mobile drawer, pills form, etc.)
      document.querySelectorAll(`.js-filter[data-filter-key="${filterKey}"]`).forEach((target) => {

        // 1. Update the checkbox/option list — the bit that changes most (counts, checked, disabled)
        const sourceList = element.querySelector('.js-facets-list');
        const targetList = target.querySelector('.js-facets-list');
        if (sourceList && targetList) targetList.innerHTML = sourceList.innerHTML;

        // 2. Update the selected-count badge inside the <summary> (desktop <details>)
        const sourceSummary = element.querySelector('.facets__summary, summary');
        const targetSummary = target.querySelector('.facets__summary, summary');
        if (sourceSummary && targetSummary) targetSummary.innerHTML = sourceSummary.innerHTML;

        // 3. Update the "N selected" header line inside the filter panel
        const sourceHeader = element.querySelector('.facets__header, .facets__header-vertical');
        const targetHeader = target.querySelector('.facets__header, .facets__header-vertical');
        if (sourceHeader && targetHeader) targetHeader.innerHTML = sourceHeader.innerHTML;

        // 4. Update the mobile accordion title count badge if present
        const sourceTitle = element.querySelector('.filter_active_values_count');
        const targetTitle = target.querySelector('.filter_active_values_count');
        if (sourceTitle && targetTitle) targetTitle.innerHTML = sourceTitle.innerHTML;
      });
    });

    // Sync ALL price range inputs (desktop + mobile) from the server response so stale
    // price values don't persist in any form after a non-price filter change.
    const priceMinSource = parsedHTML.querySelector('input[name="filter.v.price.gte"]');
    const priceMaxSource = parsedHTML.querySelector('input[name="filter.v.price.lte"]');
    document.querySelectorAll('input[name="filter.v.price.gte"]').forEach((el) => {
      el.value = priceMinSource ? priceMinSource.value : '';
    });
    document.querySelectorAll('input[name="filter.v.price.lte"]').forEach((el) => {
      el.value = priceMaxSource ? priceMaxSource.value : '';
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const countsTarget = event && event.target ? event.target.closest('.js-filter') : null;
      if (countsTarget) FacetFiltersForm.renderCounts(countsToRender, countsTarget);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm && mobileForm.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') menuDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }

    // Update the count badge in the accordion title (e.g. mobile price filter)
    const sourceCount = source.querySelector('.filter_active_values_count');
    const targetCount = target.querySelector('.filter_active_values_count');
    if (sourceCount && targetCount) targetCount.innerHTML = sourceCount.innerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const nostoSearchGrid = window.__nostoSearchGrid;
    if (nostoSearchGrid && nostoSearchGrid.ready) {
      nostoSearchGrid.applyFilters(searchParams);
      return;
    }

    const nostoCollectionGrid = window.__nostoCollectionGrid;
    if (nostoCollectionGrid && nostoCollectionGrid.ready) {
      nostoCollectionGrid.applyFilters(searchParams);
      return;
    }

    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    // Determine if this event came from the price-range section.
    // Desktop wraps inputs in <price-range>; mobile wrapper has class facet-price.
    const isPriceEvent = !!event.target.closest('price-range, .facet-price');

    if (event.target.className == 'mobile-facets__checkbox') {
      let searchParams = this.createSearchParams(event.target.closest('form'));

      // Always sync price params from DOM input values so empty inputs remove price from URL.
      const p = new URLSearchParams(searchParams);
      const gteVal = document.querySelector('input[name="filter.v.price.gte"]')?.value || '';
      const lteVal = document.querySelector('input[name="filter.v.price.lte"]')?.value || '';
      if (gteVal) { p.set('filter.v.price.gte', gteVal); } else { p.delete('filter.v.price.gte'); }
      if (lteVal) { p.set('filter.v.price.lte', lteVal); } else { p.delete('filter.v.price.lte'); }
      searchParams = p.toString();

      this.onSubmitForm(searchParams, event);
    } else {
      const eventForm = event.target.closest('form');
      const isMobile = eventForm.id === 'FacetFiltersFormMobile';

      const forms = [];

      if (isMobile) {
        // Mobile drawer has everything (filters + sort) in one form.
        forms.push(this.createSearchParams(eventForm));
      } else {
        // Desktop: always include FacetFiltersForm which holds all active filter checkboxes.
        const desktopFilterForm = document.getElementById('FacetFiltersForm');
        if (desktopFilterForm) {
          const noJsElements = document.querySelectorAll('.no-js-list');
          noJsElements.forEach((el) => el.remove());
          forms.push(this.createSearchParams(desktopFilterForm));
        }

        // If the event came from a different form (e.g. the no-ID sort form used in
        // vertical layout, or a drawer sort form), also serialize it so sort_by is included.
        if (eventForm.id !== 'FacetFiltersForm') {
          forms.push(this.createSearchParams(eventForm));
        }
      }

      const merged = new URLSearchParams(forms.join('&'));

      // Always sync price params from DOM input values so empty inputs remove price from URL.
      const gteVal = document.querySelector('input[name="filter.v.price.gte"]')?.value || '';
      const lteVal = document.querySelector('input[name="filter.v.price.lte"]')?.value || '';
      if (gteVal) { merged.set('filter.v.price.gte', gteVal); } else { merged.delete('filter.v.price.gte'); }
      if (lteVal) { merged.set('filter.v.price.lte', lteVal); } else { merged.delete('filter.v.price.lte'); }

      this.onSubmitForm(merged.toString(), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    const nostoSearchGrid = window.__nostoSearchGrid;
    if (nostoSearchGrid && nostoSearchGrid.ready) {
      nostoSearchGrid.applyFilters(url);
      return;
    }

    const nostoCollectionGrid = window.__nostoCollectionGrid;
    if (nostoCollectionGrid && nostoCollectionGrid.ready) {
      nostoCollectionGrid.applyFilters(url);
      return;
    }

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink = this.querySelector('a');

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();

      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
